import { CREATED, NO_CONTENT, OK } from 'http-status-codes';
import { call, put, takeLatest } from 'redux-saga/effects';

import * as api from './api';
import { actions, types } from './duck';

const getErrorMessage = response =>
  (response && response.data && response.data.detail) ||
  (response && response.statusText) ||
  'Something went wrong';

export function* getBlogsSaga() {
  try {
    yield put(actions.getBlogsRequest());

    const response = yield call(api.getBlogs);

    if (response.status === OK) {
      yield put(actions.getBlogsSuccess(response.data));
    } else {
      yield put(actions.getBlogsFailure(getErrorMessage(response)));
    }
  } catch (error) {
    yield put(actions.getBlogsFailure(error.message));
  }
}

export function* getBlogSaga({ id }) {
  try {
    yield put(actions.getBlogRequest());

    const response = yield call(api.getBlog, id);

    if (response.status === OK) {
      yield put(actions.getBlogSuccess(response.data));
    } else {
      yield put(actions.getBlogFailure(getErrorMessage(response)));
    }
  } catch (error) {
    yield put(actions.getBlogFailure(error.message));
  }
}

export function* getBlogBySlugSaga({ slug }) {
  try {
    yield put(actions.getBlogRequest());

    const response = yield call(api.getBlogBySlug, slug);

    if (response.status === OK) {
      yield put(actions.getBlogSuccess(response.data));
    } else {
      yield put(actions.getBlogFailure(getErrorMessage(response)));
    }
  } catch (error) {
    yield put(actions.getBlogFailure(error.message));
  }
}

export function* createBlogSaga({ data }) {
  try {
    yield put(actions.createBlogRequest());

    const response = yield call(api.createBlog, data);

    if (response.status === CREATED || response.status === OK) {
      yield put(actions.createBlogSuccess(response.data));
      yield put(actions.getBlogs());
    } else {
      yield put(actions.createBlogFailure(getErrorMessage(response)));
    }
  } catch (error) {
    yield put(actions.createBlogFailure(error.message));
  }
}

export function* updateBlogSaga({ id, data }) {
  try {
    yield put(actions.updateBlogRequest());

    const response = yield call(api.updateBlog, id, data);

    if (response.status === OK) {
      yield put(actions.updateBlogSuccess(response.data));
      yield put(actions.getBlogs());
    } else {
      yield put(actions.updateBlogFailure(getErrorMessage(response)));
    }
  } catch (error) {
    yield put(actions.updateBlogFailure(error.message));
  }
}

export function* deleteBlogSaga({ id }) {
  try {
    yield put(actions.deleteBlogRequest());

    const response = yield call(api.deleteBlog, id);

    if (response.status === NO_CONTENT || response.status === OK) {
      yield put(actions.deleteBlogSuccess({ id }));
      yield put(actions.getBlogs());
    } else {
      yield put(actions.deleteBlogFailure(getErrorMessage(response)));
    }
  } catch (error) {
    yield put(actions.deleteBlogFailure(error.message));
  }
}

export function* getBlogAuthorsSaga() {
  try {
    yield put(actions.getBlogAuthorsRequest());    
    const response = yield call(api.getBlogAuthors);    

    if (response.status === OK) {
      yield put(actions.getBlogAuthorsSuccess(response.data));
    } else {
      yield put(actions.getBlogAuthorsFailure(getErrorMessage(response)));
    }
  } catch (error) {
    yield put(actions.getBlogAuthorsFailure(error.message));
  }
}

export function* getBlogAuthorSaga({ id }) {
  try {
    yield put(actions.getBlogAuthorRequest());

    const response = yield call(api.getBlogAuthor, id);

    if (response.status === OK) {
      yield put(actions.getBlogAuthorSuccess(response.data));
    } else {
      yield put(actions.getBlogAuthorFailure(getErrorMessage(response)));
    }
  } catch (error) {
    yield put(actions.getBlogAuthorFailure(error.message));
  }
}

export function* createBlogAuthorSaga({ data }) {
  try {
    yield put(actions.createBlogAuthorRequest());

    const response = yield call(api.createBlogAuthor, data);

    if (response.status === CREATED || response.status === OK) {
      yield put(actions.createBlogAuthorSuccess(response.data));
      yield put(actions.getBlogAuthors());
    } else {
      yield put(actions.createBlogAuthorFailure(getErrorMessage(response)));
    }
  } catch (error) {
    console.log('[createBlogAuthor] error:', error);
    yield put(actions.createBlogAuthorFailure(error.message));
  }
}

export function* updateBlogAuthorSaga({ id, data }) {
  try {
    yield put(actions.updateBlogAuthorRequest());

    const response = yield call(api.updateBlogAuthor, id, data);

    if (response.status === OK) {
      yield put(actions.updateBlogAuthorSuccess(response.data));
      yield put(actions.getBlogAuthors());
    } else {
      yield put(actions.updateBlogAuthorFailure(getErrorMessage(response)));
    }
  } catch (error) {
    console.log('[updateBlogAuthorsSaga] error:', error);
    yield put(actions.updateBlogAuthorFailure(error.message));
  }
}

export function* deleteBlogAuthorSaga({ id }) {
  try {
    yield put(actions.deleteBlogAuthorRequest());

    const response = yield call(api.deleteBlogAuthor, id);

    if (response.status === NO_CONTENT || response.status === OK) {
      yield put(actions.deleteBlogAuthorSuccess({ id }));
      yield put(actions.getBlogAuthors());
    } else {
      yield put(actions.deleteBlogAuthorFailure(getErrorMessage(response)));
    }
  } catch (error) {
    yield put(actions.deleteBlogAuthorFailure(error.message));
  }
}

export function* getBlogBusinessesSaga() {
  try {
    yield put(actions.getBlogBusinessesRequest());

    const response = yield call(api.getBlogBusinesses);

    if (response.status === OK) {
      yield put(actions.getBlogBusinessesSuccess(response.data));
    } else {
      yield put(
        actions.getBlogBusinessesFailure(getErrorMessage(response)),
      );
    }
  } catch (error) {
    yield put(actions.getBlogBusinessesFailure(error.message));
  }
}

export default function* saga() {
  yield takeLatest(types.GET_BLOGS, getBlogsSaga);
  yield takeLatest(types.GET_BLOG, getBlogSaga);
  yield takeLatest(types.GET_BLOG_BY_SLUG, getBlogBySlugSaga);
  yield takeLatest(types.CREATE_BLOG, createBlogSaga);
  yield takeLatest(types.UPDATE_BLOG, updateBlogSaga);
  yield takeLatest(types.DELETE_BLOG, deleteBlogSaga);

  yield takeLatest(types.GET_BLOG_AUTHORS, getBlogAuthorsSaga);
  yield takeLatest(types.GET_BLOG_AUTHOR, getBlogAuthorSaga);
  yield takeLatest(types.CREATE_BLOG_AUTHOR, createBlogAuthorSaga);
  yield takeLatest(types.UPDATE_BLOG_AUTHOR, updateBlogAuthorSaga);
  yield takeLatest(types.DELETE_BLOG_AUTHOR, deleteBlogAuthorSaga);

  yield takeLatest(types.GET_BLOG_BUSINESSES, getBlogBusinessesSaga,);
}